/**
 * copyUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Utilitários de cópia e formatação para compartilhamento via WhatsApp.
 *
 * @module copyUtils
 */

import { WEEKDAYS_LONG, MONTHS } from '../constants/calendarConstants';

// ── Formatação de slots ───────────────────────────────────────────────────────

/**
 * Formata um slot de horário para cópia/compartilhamento.
 *
 * @param {Date} date - Data e hora do slot
 * @param {'short'|'long'|'weekday'} [format='short'] - Formato desejado
 *   - 'short'   → "21/05 às 14:00"
 *   - 'long'    → "Quinta-feira, 21 de Maio às 14:00"
 *   - 'weekday' → "Quinta-feira às 14:00"
 * @returns {string} Slot formatado
 */
export function formatSlotForCopy(date, format = 'short') {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const time = `${hours}:${minutes}`;
  const weekday = WEEKDAYS_LONG[date.getDay()];
  const monthName = MONTHS[date.getMonth()];

  switch (format) {
    case 'long':
      return `${weekday}, ${date.getDate()} de ${monthName} às ${time}`;
    case 'weekday':
      return `${weekday} às ${time}`;
    case 'short':
    default:
      return `${day}/${month} às ${time}`;
  }
}

// ── Clipboard ─────────────────────────────────────────────────────────────────

/**
 * Copia texto para o clipboard do usuário.
 * Usa a API moderna `navigator.clipboard` com fallback para `execCommand`.
 *
 * @param {string} text - Texto a copiar
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function copyToClipboard(text) {
  // Tenta API moderna primeiro
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch (err) {
      // Fallback para execCommand se a API moderna falhar
    }
  }

  // Fallback: cria elemento temporário
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    textarea.setAttribute('readonly', '');
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (success) {
      return { success: true };
    }
    return { success: false, error: 'execCommand falhou' };
  } catch (err) {
    return { success: false, error: err.message || 'Erro ao copiar' };
  }
}

// ── WhatsApp ──────────────────────────────────────────────────────────────────

/**
 * Formata uma mensagem de sugestão de horário para WhatsApp.
 *
 * @param {Date} date - Data e hora do horário sugerido
 * @param {string} [clinicName=''] - Nome da clínica/empresa
 * @returns {string} Mensagem formatada para WhatsApp
 */
export function formatWhatsAppSuggestion(date, clinicName = '') {
  const slotLong = formatSlotForCopy(date, 'long');
  const slotShort = formatSlotForCopy(date, 'short');

  const clinicHeader = clinicName ? `*${clinicName}*\n\n` : '';

  return (
    clinicHeader +
    `Olá!\n\n` +
    `Temos um horário disponível:\n\n` +
    `*${slotLong}*\n\n` +
    `Gostaria de confirmar este horário?\n` +
    `Responda esta mensagem para confirmar ou solicitar outro horário.`
  );
}

/**
 * Abre o WhatsApp Web com uma mensagem pré-preenchida.
 *
 * @param {string} phone - Número de telefone (somente dígitos, com DDI)
 * @param {string} message - Mensagem a enviar
 * @returns {void}
 */
export function openWhatsApp(phone, message) {
  const cleanPhone = phone.replace(/\D/g, '');
  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Formata lista de horários disponíveis para cópia em bloco.
 * Útil para enviar múltiplas opções de horário de uma vez.
 *
 * @param {Date[]} dates - Array de datas disponíveis
 * @param {string} [clinicName=''] - Nome da clínica
 * @returns {string} Mensagem formatada com múltiplos horários
 */
export function formatMultipleSlotsForCopy(dates, clinicName = '') {
  if (!dates || dates.length === 0) return '';

  const clinicHeader = clinicName ? `*${clinicName}*\n\n` : '';
  const slots = dates
    .map((d, i) => `${i + 1}. ${formatSlotForCopy(d, 'long')}`)
    .join('\n');

  return (
    clinicHeader +
    `Olá!\n\n` +
    `Seguem os horários disponíveis:\n\n` +
    `${slots}\n\n` +
    `Qual horário prefere? Responda com o número da opção.`
  );
}
